import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useGetTripReconciliationQuery } from "../../../store/api/vanSalesApi";
import { useGetCurrentCompanyQuery } from "../../../store/api/companyApi";

function VanReconciliation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useGetTripReconciliationQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || "USD";

  const fmt = (n) =>
    new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(Number(n || 0));

  if (isLoading) return <Typography>Loading reconciliation...</Typography>;
  if (!data) return <Typography>Reconciliation data not found.</Typography>;

  const {
    trip,
    itemReconciliation = [],
    salesSummary = [],
    cashSummary = {},
  } = data;

  const totalLoadedCost = itemReconciliation.reduce(
    (s, i) => s + i.costLoaded,
    0,
  );
  const totalSoldCost = itemReconciliation.reduce((s, i) => s + i.costSold, 0);
  const totalVarianceItems = itemReconciliation.reduce(
    (s, i) => s + i.variance,
    0,
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Trip Reconciliation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trip: {trip?.tripNumber} | Van: {trip?.vanName} ({trip?.vanPlate}) |
            Salesperson: {trip?.salesperson || "—"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/van-sales/trips/${id}`)}
          >
            Back to Trip
          </Button>
        </Box>
      </Box>

      {/* Trip Summary */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Trip Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Chip
              label={trip?.status?.replace("_", " ").toUpperCase() || "—"}
              color="success"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Warehouse
            </Typography>
            <Typography>{trip?.warehouse || "—"}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Load Date
            </Typography>
            <Typography>
              {trip?.loadDate ? new Date(trip.loadDate).toLocaleString() : "—"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Return Date
            </Typography>
            <Typography>
              {trip?.returnDate
                ? new Date(trip.returnDate).toLocaleString()
                : "—"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Item Reconciliation */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Item Reconciliation
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Loaded
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Sold
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Returned
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Damaged
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Variance
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Cost (Loaded)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Cost (Sold)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemReconciliation.map((item, idx) => (
                <TableRow key={idx} hover>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell>{item.productSku || "—"}</TableCell>
                  <TableCell align="center">{item.loaded}</TableCell>
                  <TableCell align="center">{item.sold}</TableCell>
                  <TableCell align="center">{item.returned}</TableCell>
                  <TableCell align="center">{item.damaged}</TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      color:
                        item.variance !== 0 ? "error.main" : "success.main",
                    }}
                  >
                    {item.variance}
                  </TableCell>
                  <TableCell align="right">{fmt(item.costLoaded)}</TableCell>
                  <TableCell align="right">{fmt(item.costSold)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell colSpan={6} sx={{ fontWeight: 700 }}>
                  Totals
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 700,
                    color:
                      totalVarianceItems !== 0 ? "error.main" : "success.main",
                  }}
                >
                  {totalVarianceItems}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmt(totalLoadedCost)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {fmt(totalSoldCost)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Sales Summary */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Sales Summary
        </Typography>
        {salesSummary.length === 0 ? (
          <Typography color="text.secondary">
            No sales were made during this trip.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Paid
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesSummary.map((s, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{s.invoiceNumber || s.orderNumber}</TableCell>
                    <TableCell>{s.customerName}</TableCell>
                    <TableCell align="right">{fmt(s.totalAmount)}</TableCell>
                    <TableCell align="right">{fmt(s.paidAmount)}</TableCell>
                    <TableCell align="right">{fmt(s.balanceAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Cash Reconciliation */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Cash Reconciliation
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "primary.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "primary.200",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Cash Expected
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {fmt(cashSummary.expected)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "success.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "success.200",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Cash Collected
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {fmt(cashSummary.collected)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: cashSummary.variance >= 0 ? "success.50" : "error.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor:
                  cashSummary.variance >= 0 ? "success.200" : "error.200",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Cash Variance
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color:
                    cashSummary.variance >= 0 ? "success.main" : "error.main",
                }}
              >
                {fmt(cashSummary.variance)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default VanReconciliation;
